import { Button } from "@gigpro/ui/Button";
import { Input } from "@gigpro/ui/Input";
import { Label } from "@gigpro/ui/Label";
import { ApiError } from "@sidegigllc/api/resources";
import React from "react";
import { flushSync } from "react-dom";
import { InvoicedCustomerIdErrorDialog } from "./InvoicedCustomerIdErrorDialog";
import { useSaveInvoicedCustomerId } from "./useSaveInvoicedCustomerId";
import type { PaymentSourceId } from "./useSaveStripePaymentMethod";

const FALLBACK_ERROR = "Something went wrong saving the customer ID. Please try again.";

type InvoicedCustomerIdFieldProps = {
  sourceId: PaymentSourceId;
  /** The id stored on the entity right now; `null`/`undefined` when none is set or it hasn't loaded. */
  savedId: string | null | undefined;
};

/**
 * Admin-only editor for the Invoiced.com customer an org/location invoices through. It saves on its
 * own PUT, deliberately outside the dialog's react-hook-form: the id is never part of the
 * "Save Payment Method" PATCH, and a draft here never trips the dialog's unsaved-changes blocker.
 *
 * It renders a plain `Input` + `Label` rather than `Textfield` for the same reason — `Textfield`
 * binds to a form control, and the only form in scope is the dialog's.
 */
export function InvoicedCustomerIdField({ sourceId, savedId }: Readonly<InvoicedCustomerIdFieldProps>) {
  const inputId = React.useId();
  const labelId = React.useId();
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const editButtonRef = React.useRef<HTMLButtonElement>(null);
  // Lets the remounted Edit button take focus, which otherwise drops to <body> with Save/Cancel.
  const focusEditOnMount = React.useRef(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const update = useSaveInvoicedCustomerId(sourceId);

  const leaveEditMode = () => {
    focusEditOnMount.current = true;
    setIsEditing(false);
  };

  const startEditing = () => {
    // Seed from the current prop so an id saved elsewhere isn't replaced by a stale draft.
    setDraft(savedId ?? "");
    // The input stays mounted, so `autoFocus` never fires; commit it enabled, then focus it.
    flushSync(() => setIsEditing(true));
    inputRef.current?.focus();
  };

  const save = () => {
    if (update.isPending) return;
    update.save(draft.trim(), {
      onSuccess: leaveEditMode,
      onError: (error) => {
        const apiError = ApiError.fromThrown(error);
        setErrorMessage(apiError.isUnknownError() ? FALLBACK_ERROR : apiError.getMainErrorMessage());
      },
    });
  };

  return (
    <fieldset aria-labelledby={labelId} className="flex min-w-0 flex-col gap-1">
      <Label id={labelId} htmlFor={inputId}>
        Invoiced.com customer ID
      </Label>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <Input
            id={inputId}
            // Read-only look matches the contact-emails field; Input's own disabled background yields to these.
            className={isEditing ? undefined : "bg-white opacity-50 [&>input]:bg-white"}
            inputClassName={isEditing ? undefined : "cursor-not-allowed"}
            ref={inputRef}
            value={isEditing ? draft : (savedId ?? "")}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              // Enter would otherwise implicitly submit the surrounding payment-method form.
              if (event.key !== "Enter") return;
              event.preventDefault();
              save();
            }}
            disabled={!isEditing}
            readOnly={update.isPending}
          />
        </div>
        {isEditing ? (
          <>
            <Button size="small" onClick={leaveEditMode} disabled={update.isPending}>
              Cancel
            </Button>
            <Button variant="contained" size="small" onClick={save} loading={update.isPending}>
              Save
            </Button>
          </>
        ) : (
          <Button
            size="small"
            onClick={startEditing}
            ref={(button) => {
              editButtonRef.current = button;
              if (button && focusEditOnMount.current) {
                focusEditOnMount.current = false;
                button.focus();
              }
            }}
          >
            Edit
          </Button>
        )}
      </div>
      <InvoicedCustomerIdErrorDialog
        open={errorMessage !== undefined}
        message={errorMessage ?? ""}
        onClose={() => {
          // Back to read-only on the id that is actually stored — the rejected draft is discarded.
          setErrorMessage(undefined);
          setIsEditing(false);
        }}
        onCloseAutoFocus={(event) => {
          // Radix would refocus Save, which has unmounted; land on Edit inside the payment dialog.
          event.preventDefault();
          editButtonRef.current?.focus();
        }}
      />
    </fieldset>
  );
}
